import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import { FC, memo, useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared/components/Button/Button';
import { PasswordInput } from 'shared/components/PasswordInput';
import * as yup from 'yup';

interface PasswordStepInputs {
  password: string;
}

interface PasswordStepProps {
  onPasswordSubmit: (password: string) => Promise<string>;
  onForgotPasswordClick: () => void;
  loading?: boolean;
}

const passwordInputStepSchema = (t: TFunction) =>
  yup.object({
    password: yup.string().required(t('auth:validationMessages.fieldRequired') ?? ''),
  });

export const PasswordStep: FC<PasswordStepProps> = memo(
  ({ onPasswordSubmit, onForgotPasswordClick, loading }) => {
    const { t } = useTranslation();
    const {
      register,
      handleSubmit,
      setError,
      formState: { errors },
    } = useForm<PasswordStepInputs>({ resolver: yupResolver(passwordInputStepSchema(t)) });

    const onSubmit: SubmitHandler<PasswordStepInputs> = useCallback(
      async ({ password }) => {
        try {
          await onPasswordSubmit(password);
        } catch (err) {
          if (err instanceof Error) {
            setError('password', {
              message: err.message,
            });
            return;
          }
          setError('password', {
            message: t('auth:validationMessages.unknownError'),
          });
        }
      },
      [onPasswordSubmit, setError],
    );

    return (
      <form
        className="flex w-[27vw] min-w-[343px] flex-col gap-6 pt-0 md:min-w-[400px] md:max-w-[750px] md:pt-17"
        onSubmit={handleSubmit(onSubmit)}
      >
        <h2 className="text-left text-2xl font-bold">{t('auth:enterPasswordTitle')}</h2>
        <PasswordInput
          {...register('password')}
          label={t('auth:labels.password')}
          error={errors.password}
          autoFocus
        />
        <Button
          fullWidth
          size="lg"
          name="signInButton"
          type="submit"
          disabled={loading}
          loading={loading}
        >
          {t('action:next')}
        </Button>
        <div className="justify-left flex">
          <Button variant="text" name="resetPasswordButton" onClick={onForgotPasswordClick}>
            {t('auth:forgotPassword')}
          </Button>
        </div>
      </form>
    );
  },
);
